import { Injectable } from '@angular/core';
import { ApiService } from './api.service';

export interface TeamMemberAccessResult {
  has_access: boolean;
  client_access: boolean | null;
  company_access: boolean | null;
}

// Whether a team member can see a contact / company - used to warn (never block) when something is being given to a team
// member who wouldn't be able to see the client it is for. Uses the same bulk "permission_check" calls as the project create
// page: clients / companies?team_member=...&permission_check=1&guids=... lists the ones that team member is allowed to see.
@Injectable({
  providedIn: 'root',
})
export class TeamMemberAccessService {

  // Long enough to stop the same check repeating while a form is open, short enough that a change to someone's access shows up soon
  private static readonly CACHE_MS = 30 * 1000;
  private cache = new Map<string, { loaded: number, result: TeamMemberAccessResult }>();

  constructor(private apiService: ApiService) { }

  // member is the team member record ({ guid, is_admin }). Returns null when the check couldn't be made - callers should then
  // simply show no warning.
  async Check(member: any, client_guid: string, company_guid: string): Promise<TeamMemberAccessResult | null> {
    if (!member?.guid || (!client_guid && !company_guid)) return null;
    // Firm admins can see everything
    if (member.is_admin == 1 || member.is_admin === true) return { has_access: true, client_access: client_guid ? true : null, company_access: company_guid ? true : null };

    const key = `${member.guid}|${client_guid || ''}|${company_guid || ''}`;
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.loaded < TeamMemberAccessService.CACHE_MS) return cached.result;

    try {
      const [client_data, company_data]: any[] = await Promise.all([
        client_guid ? this.apiService.get(`clients?team_member=${member.guid}&permission_check=1&guids=${encodeURIComponent(client_guid)}`) : Promise.resolve(null),
        company_guid ? this.apiService.get(`companies?team_member=${member.guid}&permission_check=1&guids=${encodeURIComponent(company_guid)}`) : Promise.resolve(null),
      ]);
      if ((client_guid && !client_data?.success) || (company_guid && !company_data?.success)) return null;

      const client_access = client_guid ? (client_data.data || []).some((c: any) => c.guid === client_guid) : null;
      const company_access = company_guid ? (company_data.data || []).some((c: any) => c.guid === company_guid) : null;
      const result: TeamMemberAccessResult = { has_access: client_access !== false && company_access !== false, client_access, company_access };
      this.cache.set(key, { loaded: Date.now(), result });
      return result;
    } catch {
      return null;
    }
  }
}
